import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Tooltip } from 'react-tooltip';
import { offset, shift } from '@floating-ui/dom';
import axios from 'axios';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  checkLogin,
  setOtpVerified,
  setAdminSupplierInfo,
  setAdminPgMerchantInfo,
  setCpSupplierInfo,
  setCpPgMerchantInfo,
} from '../../store';
import { Outlet } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { useTooltip } from '../../context/TooltipContext';
import { Menu, X, Truck, Lock, CreditCard, Upload, ImageIcon, HelpCircle, Activity, BarChart3, Settings2, Save, Wallet } from 'lucide-react';
import {
  getLoginLogoAssetUrl,
  getLoginBackgroundAssetUrl,
  getHeaderLogoAssetUrl,
  SITE_ASSETS_VERSION_KEY,
  getHeaderLogoStyle,
} from '../../constants/adminImages';
import { BRAND_COPYRIGHT } from '../../config/brandConfig';
import { ADMIN_CP_PAYOUT_TENANT_ENABLED, ADMIN_SINGLE_CP_MODE, MERCHANT_MANUAL_PAYMENT_ENABLED } from '../../config/menuConfig';
import { uploadSiteAssetsMulti } from '../../../Admin/api/siteAssets';

// 0: 기존 모계좌 잔액 UI, 1: 다중 PG 모계좌/한도 UI (목업 기반)
// TEST 값만 바꾸면 env 재시작 없이 화면 확인 가능(빌드에는 null로 두세요).
const TEST_PG_HEADER_MODE = null; // 0 or 1
const PG_HEADER_MODE =
  TEST_PG_HEADER_MODE != null
    ? Number(TEST_PG_HEADER_MODE)
    : process.env.REACT_APP_PG_HEADER_MODE === '1'
      ? 1
      : 0;

// 0: 한도 없는 버전(PG 선택만), 1: 한도 있는 버전(현재 UI)
const TEST_PG_LIMIT_MODE = null; // 0 or 1
const PG_LIMIT_MODE =
  TEST_PG_LIMIT_MODE != null
    ? Number(TEST_PG_LIMIT_MODE)
    : process.env.REACT_APP_PG_LIMIT_MODE === '1'
      ? 1
      : 0;

function toLocalYmd(date) {
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("");
}

function formatHeaderBalance(value) {
  const normalized = String(value ?? "").replace(/,/g, "").trim();
  const digits = normalized.startsWith("-") ? normalized.slice(1) : normalized;
  if (!digits || [...digits].some((char) => char < "0" || char > "9")) return null;
  try {
    return window.BigInt(normalized).toLocaleString("ko-KR") + "원";
  } catch {
    return null;
  }
}

// 동적 import로 각 폴더의 API 로드 (React.lazy 대신 직접 함수 로더 사용)
async function loadAdminLogout() {
  try {
    const m = await import('../../../Admin/api/adminAuth');
    return m.adminLogout || (async () => { });
  } catch {
    return async () => { };
  }
}

async function loadAdminChangePassword() {
  try {
    const m = await import('../../../Admin/api/adminAuth');
    return m.adminChangePassword || (async () => { });
  } catch {
    return async () => { };
  }
}

async function loadCpChangePassword() {
  try {
    const m = await import('../../../Merchant/api/cp');
    return m.cpChangePassword || (async () => { });
  } catch {
    return async () => { };
  }
}

async function loadCpLogout() {
  try {
    const m = await import('../../../Merchant/api/cp');
    return m.cpLogout || (async () => { });
  } catch {
    return async () => { };
  }
}

async function loadCpTerminalList() {
  try {
    const m = await import('../../../Merchant/api/cp');
    return m.getCpTerminalList || (async () => ({ rows: [] }));
  } catch {
    return async () => ({ rows: [] });
  }
}

async function loadTerminalChangePassword() {
  try {
    const m = await import('../../../Merchant/api/cp');
    return m.terminalChangePassword || (async () => { });
  } catch {
    return async () => { };
  }
}

// 앱 로그아웃 로더 - /api/cp/terminal/logout 사용
async function loadAppTerminalLogout() {
  try {
    const m = await import('../../../App/api/cp');
    return m.terminalLogout || (async () => { });
  } catch {
    return async () => { };
  }
}

const SESSION_EXPIRY_KEY = 'session_expiry';
/** 가맹점 페이지 브랜드명 - 환경 변수로 변경 가능 */
const MERCHANT_BRAND_NAME = process.env.REACT_APP_MERCHANT_BRAND_NAME || '-';
/** 관리자 페이지 브랜드명 - 환경 변수로 변경 가능 */
const ADMIN_BRAND_NAME = process.env.REACT_APP_ADMIN_BRAND_NAME || process.env.REACT_APP_MERCHANT_BRAND_NAME || '-';
/** 가맹점 페이지 버전: 0(기존), 1(헤더 중앙 메뉴형) */
const TEST_MERCHANT_PAGE_VERSION = null; // 0 or 1
const MERCHANT_PAGE_VERSION =
  TEST_MERCHANT_PAGE_VERSION != null
    ? Number(TEST_MERCHANT_PAGE_VERSION)
    : process.env.REACT_APP_MERCHANT_PAGE_VERSION === '1'
      ? 1
      : 0;
/** 세션 최대 1시간 — 표시/연장 모두 이 값으로 제한 */
const SESSION_MAX_SECONDS = 60 * 60;

/** 수기결제 팝업 옵션 */
function openSugiPopup() {
  const href = `${window.location.origin}/cp/sugi`;
  const w = 450;
  const h = 800;
  // 현재 브라우저 창 위치/크기 기준으로 정중앙 계산
  const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualScreenTop = window.screenTop ?? window.screenY ?? 0;
  const winWidth =
    window.innerWidth ??
    document.documentElement.clientWidth ??
    window.screen?.width ??
    800;
  const winHeight =
    window.innerHeight ??
    document.documentElement.clientHeight ??
    window.screen?.height ??
    600;
  const left = dualScreenLeft + Math.max(0, (winWidth - w) / 2);
  const top = dualScreenTop + Math.max(0, (winHeight - h) / 2);
  const popupOptions = `width=${w},height=${h},scrollbars=yes,resizable=yes,left=${Math.round(
    left,
  )},top=${Math.round(top)}`;
  window.open(href, 'sugiPopup', popupOptions);
}

/** 저장된 만료 시각 기준 남은 초(초기값). 1시간 초과 시 저장소를 지금+1시간으로 덮어 60분→0 1초씩 감소 */
function getInitialSessionSeconds() {
  if (typeof window === 'undefined') return 0;
  const exp = sessionStorage.getItem(SESSION_EXPIRY_KEY) || localStorage.getItem('ls_session_expiry');
  if (!exp) return 0;
  let left = Math.max(0, Math.ceil((Number(exp) - Date.now()) / 1000));
  if (left > SESSION_MAX_SECONDS) {
    const cappedExpiry = String(Date.now() + SESSION_MAX_SECONDS * 1000);
    try {
      sessionStorage.setItem(SESSION_EXPIRY_KEY, cappedExpiry);
      localStorage.setItem('ls_session_expiry', cappedExpiry);
    } catch (e) { }
    left = SESSION_MAX_SECONDS;
  }
  return left;
}

/** 60:00 ~ 0:00 형식 (60분부터 1초씩 감소) */
function formatSessionTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * 로그인 제외 모든 페이지 공통 레이아웃
 * - 사이드바 + 헤더를 이 파일에서 관리 (UI 수정 시 이 파일만 편집)
 * @param {Array} menuItems - { path, label, icon } 형태의 메뉴 배열
 * @param {string} basePath - 라우트 베이스 경로 (/admin 또는 /cp)
 * @param {string} userLabel - 사용자 표시명 (예: 관리자님, 가맹점명)
 */
function MainLayout({ menuItems = [], basePath = '/admin', userLabel = '관리자님' }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { showAllOpen, toggleShowAll, resetShowAll } = useTooltip();
  const [hoveredTooltipId, setHoveredTooltipId] = useState(null);
  const adminSupplierInfo = useSelector((state) => state.adminSupplierInfo?.info);
  const adminSupplierInfoLoaded = useSelector((state) => state.adminSupplierInfo?.loaded);
  const adminPgMerchantRows = useSelector((state) => state.adminPgMerchantInfo?.rows) || [];
  const adminPgMerchantLoaded = useSelector((state) => state.adminPgMerchantInfo?.loaded);
  const cpSupplierInfo = useSelector((state) => state.cpSupplierInfo?.info);
  const cpSupplierInfoLoaded = useSelector((state) => state.cpSupplierInfo?.loaded);
  const cpPgMerchantLoaded = useSelector((state) => state.cpPgMerchantInfo?.loaded);
  const isMerchant = basePath === '/cp';
  const isMerchantV1 = isMerchant && MERCHANT_PAGE_VERSION === 1;

  useEffect(() => {
    if (!showAllOpen) setHoveredTooltipId(null);
  }, [showAllOpen]);

  // 모계좌 잔액 (API 조회)
  const [masterAccountBalance, setMasterAccountBalance] = useState(null); // numeric string | null
  const [masterAccountBalanceLoading, setMasterAccountBalanceLoading] = useState(false);
  const [masterAccountBalanceSource, setMasterAccountBalanceSource] = useState("LEGACY");
  const [masterAccountBalanceNoData, setMasterAccountBalanceNoData] = useState(false);
  const [masterAccountBalancePeriod, setMasterAccountBalancePeriod] = useState("");

  // 다중 PG 헤더/모달용 상태 (PG_HEADER_MODE === 1일 때만 UI에 사용, 현재는 목업 데이터)
  const [pgHeaderModalOpen, setPgHeaderModalOpen] = useState(false);
  const [pgList, setPgList] = useState([
    // { id: 1, pgProvider: 'HECTO_FINANCIAL', name: '토스페이먼츠', limit: 2000000000, usage: 1250000000, balance: 450000000, isActive: true },
    // { id: 2, pgProvider: 'PAYDATAKR', name: '나이스페이', limit: 1000000000, usage: 200000000, balance: 320000000, isActive: false },
    // { id: 3, pgProvider: 'KCP', name: 'KCP결제', limit: 500000000, usage: 480000000, balance: 139190000, isActive: false },
  ]);
  const pgFallbackProfiles = [
    { limit: 2000000000, usage: 1250000000, balance: 0 },
    { limit: 1000000000, usage: 200000000, balance: 0 },
    { limit: 500000000, usage: 480000000, balance: 0 },
  ];
  const [editingPgId, setEditingPgId] = useState(null);
  const [pgSwitchLoading, setPgSwitchLoading] = useState(false);
  const [pgStatusLoading, setPgStatusLoading] = useState(false);
  const [pgStatusLoaded, setPgStatusLoaded] = useState(false);
  const [pgActiveProviderFromStatus, setPgActiveProviderFromStatus] = useState(null);
  const [pgBalancesByProvider, setPgBalancesByProvider] = useState({});

  const activePG = useMemo(
    () => pgList.find((pg) => pg.isActive) || null,
    [pgList],
  );

  const pgHeaderIsReady = basePath === '/admin' && PG_HEADER_MODE === 1 && pgStatusLoaded && !!activePG;
  const pgHeaderItems = pgList.length
    ? pgList
    : [1, 2, 3].map((i) => ({
      id: i,
      pgProvider: '',
      name: `PG ${i}`,
      limit: 0,
      usage: 0,
      balance: null,
      isActive: false,
    }));

  const pgTotalStats = useMemo(
    () =>
      pgList.reduce(
        (acc, cur) => ({
          limit: acc.limit + cur.limit,
          usage: acc.usage + cur.usage,
          balance: acc.balance + (Number(cur.balance ?? 0) || 0),
        }),
        { limit: 0, usage: 0, balance: 0 },
      ),
    [pgList],
  );

  const formatToEok = (val) => `${(val / 100000000).toFixed(1)}억`;

  /** PG 현황·잔액 재조회 (최초 로드 / PG 전환 후) */
  const refreshPgSwitchStatus = useCallback(async () => {
    if (basePath !== '/admin') return false;
    const baseUrl = process.env.REACT_APP_API || '';
    try {
      const res = await axios.get(`${baseUrl}/api/admin/pg-switch/status`, { withCredentials: true });
      if (res?.data?.code !== 1000) {
        setPgActiveProviderFromStatus(null);
        setPgBalancesByProvider({});
        return false;
      }
      const d = res?.data?.data;
      const active = String(d?.activePgProvider || '').toUpperCase();
      const balancesRaw = d?.balances;
      const nextBalances = {};
      if (balancesRaw && typeof balancesRaw === 'object') {
        Object.entries(balancesRaw).forEach(([providerKey, v]) => {
          const key = String(providerKey || '').toUpperCase();
          const rawBalance = v?.balance;
          if (
            rawBalance === undefined
            || rawBalance === null
            || rawBalance === ''
            || rawBalance === 'null'
          ) {
            nextBalances[key] = null;
          } else {
            const parsed = Number(rawBalance);
            nextBalances[key] = Number.isFinite(parsed) ? parsed : null;
          }
        });
      }
      setPgBalancesByProvider(nextBalances);
      setPgActiveProviderFromStatus(active || null);
      return true;
    } catch {
      setPgActiveProviderFromStatus(null);
      setPgBalancesByProvider({});
      return false;
    }
  }, [basePath]);

  const handlePgHeaderSwitch = async (id) => {
    if (editingPgId || pgSwitchLoading) return;

    const target = pgList.find((pg) => pg.id === id);
    const targetPgProvider = String(target?.pgProvider || '').toUpperCase();
    if (!targetPgProvider) return;

    const reason = '한도초과';
    const baseUrl = process.env.REACT_APP_API || '';
    setPgSwitchLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}/api/admin/pg-switch/switch`,
        { pgProvider: targetPgProvider, reason },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
      );

      if (res?.data?.code !== 1000) {
        showToast(res?.data?.msg || 'PG 전환에 실패했습니다.', 'error');
        return;
      }

      const activePgProvider = String(res?.data?.data?.activePgProvider || targetPgProvider).toUpperCase();
      setPgList((prev) =>
        prev.map((pg) => ({
          ...pg,
          isActive: String(pg.pgProvider || '').toUpperCase() === activePgProvider,
        })),
      );
      setPgActiveProviderFromStatus(activePgProvider);
      await refreshPgSwitchStatus();
      setPgHeaderModalOpen(false);
      setEditingPgId(null);
    } catch (err) {
      showToast(err?.response?.data?.msg || 'PG 전환에 실패했습니다.', 'error');
    } finally {
      setPgSwitchLoading(false);
    }
  };

  const handlePgHeaderLimitChange = (id, value) => {
    const numericValue = parseFloat(value) || 0;
    const wonValue = Math.round(numericValue * 100000000);
    setPgList((prev) =>
      prev.map((pg) => (pg.id === id ? { ...pg, limit: wonValue } : pg)),
    );
  };

  const handlePgRoundingModeChange = (id, mode) => {
    setPgList((prev) =>
      prev.map((pg) => (pg.id === id ? { ...pg, roundingMode: mode } : pg)),
    );
  };

  const [sessionSecondsLeft, setSessionSecondsLeft] = useState(getInitialSessionSeconds);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [burgerMenuOpen, setBurgerMenuOpen] = useState(false);
  const burgerRef = useRef(null);
  const [driverPasswordModalOpen, setDriverPasswordModalOpen] = useState(false);
  const [driverTerminal, setDriverTerminal] = useState(null);
  const [driverNewPassword, setDriverNewPassword] = useState('');
  const [driverPasswordSubmitting, setDriverPasswordSubmitting] = useState(false);
  const [imageSettingsModalOpen, setImageSettingsModalOpen] = useState(false);
  const [imageFormSaving, setImageFormSaving] = useState(false);
  const [imageForm, setImageForm] = useState({
    loginLogo: '',
    loginBg: '',
    headerLogo: '',
  });
  const [pendingImageFiles, setPendingImageFiles] = useState({ loginLogo: null, loginBg: null, headerLogo: null });
  const [headerLogoHide, setHeaderLogoHide] = useState(false);
  /** 미리보기 이미지 로드 실패 시(서버에 없음 등) true → '미리보기' 문구 표시 */
  const [imagePreviewError, setImagePreviewError] = useState({ loginLogo: false, loginBg: false, headerLogo: false });
  /** 업로드 성공 시 갱신 → 헤더/로그인 등 이미지 URL 캐시 무효화용 */
  const [assetsVersion, setAssetsVersion] = useState(0);

  // 관리자 헤더 잔액 조회: KG이니시스 지급대행 활성 시 MID 잔액, 그 외에는 기존 모계좌 잔액
  useEffect(() => {
    if (basePath !== "/admin") return;
    const baseUrl = process.env.REACT_APP_API || "";
    let cancelled = false;

    const fetchLegacyBalance = async () => {
      setMasterAccountBalanceSource("LEGACY");
      setMasterAccountBalanceNoData(false);
      setMasterAccountBalancePeriod("");
      try {
        const res = await axios.get(baseUrl + "/api/admin/cp-settlement/payout/balance", { withCredentials: true });
        if (cancelled) return;
        const data = res?.data?.data;
        const raw = data?.balance ?? data?.balances?.krw ?? data?.parsed?.bickrw;
        setMasterAccountBalance(formatHeaderBalance(raw) ? String(raw).replace(/,/g, "").trim() : null);
      } catch {
        if (!cancelled) setMasterAccountBalance(null);
      }
    };

    const fetchHeaderBalance = async () => {
      setMasterAccountBalanceLoading(true);
      setMasterAccountBalance(null);
      setMasterAccountBalanceNoData(false);

      if (ADMIN_CP_PAYOUT_TENANT_ENABLED) {
        try {
          const capabilityResponse = await axios.get(baseUrl + "/api/admin/cp/payout-capabilities", { withCredentials: true });
          const capability = capabilityResponse?.data?.data;
          if (capabilityResponse?.data?.code === 1000 && capability?.enabled === true && capability?.balanceCheckEnabled === true) {
            const today = new Date();
            const startDate = toLocalYmd(new Date(today.getFullYear(), today.getMonth(), 1));
            const endDate = toLocalYmd(today);
            setMasterAccountBalanceSource("KG_INICIS");
            setMasterAccountBalancePeriod(startDate + "~" + endDate);
            try {
              const balanceResponse = await axios.get(baseUrl + "/api/admin/cp/payout-balance", {
                withCredentials: true,
                params: { startDate, endDate },
              });
              if (cancelled) return;
              const envelope = balanceResponse?.data;
              const balance = envelope?.data?.balance;
              if (envelope?.code !== 1000) throw new Error(envelope?.msg || "KG이니시스 잔액조회에 실패했습니다.");
              const raw = balance?.totalAmount;
              setMasterAccountBalance(formatHeaderBalance(raw) ? String(raw).replace(/,/g, "").trim() : null);
              const actualStartDate = String(balance?.startDate || startDate).replace(/\D/g, "");
              const actualEndDate = String(balance?.endDate || endDate).replace(/\D/g, "");
              setMasterAccountBalancePeriod(actualStartDate + "~" + actualEndDate);
            } catch (error) {
              if (cancelled) return;
              const detail = String(error?.response?.data?.msg || error?.message || "").toUpperCase();
              setMasterAccountBalance(null);
              setMasterAccountBalanceNoData(detail.includes("SD02") || detail.includes("잔액대사 내역 데이터가 없습니다"));
            }
            return;
          }
        } catch {
          // capability 확인 실패 시 기존 헤더 잔액으로 대체
        }
      }

      await fetchLegacyBalance();
    };

    fetchHeaderBalance().finally(() => {
      if (!cancelled) setMasterAccountBalanceLoading(false);
    });
    return () => { cancelled = true; };
  }, [basePath]);

  // 공급자 정보 조회 (관리자 최초 1회, 전역 저장)
  useEffect(() => {
    if (basePath !== '/admin') return;
    if (adminSupplierInfoLoaded) return;
    const baseUrl = process.env.REACT_APP_API || '';
    let cancelled = false;
    axios
      .get(`${baseUrl}/api/admin/receipt-info/supplier`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.code !== 1000) {
          dispatch(setAdminSupplierInfo(null));
          return;
        }
        const s = res?.data?.data?.supplierInfo;
        dispatch(setAdminSupplierInfo(s ?? null));
      })
      .catch(() => {
        if (!cancelled) dispatch(setAdminSupplierInfo(null));
      });
    return () => {
      cancelled = true;
    };
  }, [basePath, adminSupplierInfoLoaded, dispatch]);

  // PG사 정보 조회 (관리자 최초 1회, 전역 저장)
  useEffect(() => {
    if (basePath !== '/admin') return;
    if (adminPgMerchantLoaded) return;
    const baseUrl = process.env.REACT_APP_API || '';
    let cancelled = false;
    axios
      .get(`${baseUrl}/api/admin/receipt-info/pg-merchant`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.code !== 1000) {
          dispatch(setAdminPgMerchantInfo([]));
          return;
        }
        const rows = res?.data?.data?.rows;
        dispatch(setAdminPgMerchantInfo(Array.isArray(rows) ? rows : []));
      })
      .catch(() => {
        if (!cancelled) dispatch(setAdminPgMerchantInfo([]));
      });
    return () => {
      cancelled = true;
    };
  }, [basePath, adminPgMerchantLoaded, dispatch]);

  // 가맹점 공급자 정보 조회 (최초 1회, 전역 저장)
  useEffect(() => {
    if (basePath !== '/cp') return;
    if (cpSupplierInfoLoaded) return;
    const baseUrl = process.env.REACT_APP_API || '';
    let cancelled = false;
    axios
      .get(`${baseUrl}/api/cp/receipt-info/supplier`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.code !== 1000) {
          dispatch(setCpSupplierInfo(null));
          return;
        }
        const s = res?.data?.data?.supplierInfo;
        dispatch(setCpSupplierInfo(s ?? null));
      })
      .catch(() => {
        if (!cancelled) dispatch(setCpSupplierInfo(null));
      });
    return () => {
      cancelled = true;
    };
  }, [basePath, cpSupplierInfoLoaded, dispatch]);

  // 가맹점 PG사 정보 조회 (최초 1회, 전역 저장)
  useEffect(() => {
    if (basePath !== '/cp') return;
    if (cpPgMerchantLoaded) return;
    const baseUrl = process.env.REACT_APP_API || '';
    let cancelled = false;
    axios
      .get(`${baseUrl}/api/cp/receipt-info/pg-merchant`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.code !== 1000) {
          dispatch(setCpPgMerchantInfo([]));
          return;
        }
        const rows = res?.data?.data?.rows;
        dispatch(setCpPgMerchantInfo(Array.isArray(rows) ? rows : []));
      })
      .catch(() => {
        if (!cancelled) dispatch(setCpPgMerchantInfo([]));
      });
    return () => {
      cancelled = true;
    };
  }, [basePath, cpPgMerchantLoaded, dispatch]);

  // 멀티 PG UI 목록을 API 기준으로 동기화 (개수/이름/공급자코드)
  useEffect(() => {
    if (!Array.isArray(adminPgMerchantRows) || adminPgMerchantRows.length === 0) return;
    setPgList((prev) => {
      const prevActiveProvider = prev.find((p) => p.isActive)?.pgProvider;
      const activeProviderToUse = pgActiveProviderFromStatus;
      const next = adminPgMerchantRows.map((row, idx) => {
        const provider = String(row?.pg_provider || '').toUpperCase();
        const prevMatched = prev.find(
          (p) => String(p?.pgProvider || '').toUpperCase() === provider,
        );
        const fallback = pgFallbackProfiles[idx] || pgFallbackProfiles[pgFallbackProfiles.length - 1];
        return {
          id: prevMatched?.id ?? idx + 1,
          pgProvider: provider,
          name: row?.name || prevMatched?.name || provider || `PG ${idx + 1}`,
          limit: prevMatched?.limit ?? fallback.limit,
          usage: prevMatched?.usage ?? fallback.usage,
          // 잔액은 status API(pgBalancesByProvider)만 신뢰. null은 ?? 로 fallback.balance(0)에 덮이면 안 됨
          balance: prevMatched != null ? prevMatched.balance : null,
          isActive: false,
          roundingMode: prevMatched?.roundingMode ?? row?.rounding_mode ?? 'round',
        };
      });
      if (next.length === 0) return [];
      if (!activeProviderToUse) {
        // status 값 확인 전에는 선택된 PG가 없도록 유지
        return next.map((p) => ({ ...p, isActive: false }));
      }

      const activeIdx = next.findIndex(
        (p) => String(p.pgProvider || '').toUpperCase() === String(activeProviderToUse || '').toUpperCase(),
      );
      const finalActiveIdx = activeIdx >= 0 ? activeIdx : -1;
      return next.map((p, i) => ({ ...p, isActive: i === finalActiveIdx }));
    });
  }, [adminPgMerchantRows, pgActiveProviderFromStatus]);

  // PG 현황 조회 (최초 1회)
  useEffect(() => {
    if (basePath !== '/admin') return;
    if (pgStatusLoaded) return;

    let cancelled = false;
    setPgStatusLoading(true);
    refreshPgSwitchStatus().finally(() => {
      if (cancelled) return;
      setPgStatusLoaded(true);
      setPgStatusLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [basePath, pgStatusLoaded, refreshPgSwitchStatus]);

  // 상태값이 바뀌면 pgList isActive도 즉시 갱신
  useEffect(() => {
    if (basePath !== '/admin') return;
    if (!pgStatusLoaded) return;

    if (!pgActiveProviderFromStatus) {
      setPgList((prev) => prev.map((pg) => ({ ...pg, isActive: false })));
      return;
    }

    setPgList((prev) =>
      prev.map((pg) => ({
        ...pg,
        isActive: String(pg.pgProvider || '').toUpperCase() === String(pgActiveProviderFromStatus || '').toUpperCase(),
      })),
    );
  }, [basePath, pgActiveProviderFromStatus, pgStatusLoaded]);

  // PG 잔액(balances.balance) 동기화
  useEffect(() => {
    if (basePath !== '/admin') return;
    if (!pgStatusLoaded) return;

    setPgList((prev) =>
      prev.map((pg) => {
        const key = String(pg.pgProvider || '').toUpperCase();
        if (!Object.prototype.hasOwnProperty.call(pgBalancesByProvider, key)) {
          return { ...pg, balance: null };
        }
        return { ...pg, balance: pgBalancesByProvider[key] };
      }),
    );
  }, [basePath, pgStatusLoaded, pgBalancesByProvider]);

  /** 조회는 고정 경로만 사용 (REACT_APP_API 기준) */
  function getFixedAssetUrls() {
    const base = (process.env.REACT_APP_API || '').replace(/\/$/, '');
    return {
      loginLogo: base ? `${base}/public/site-assets/login-logo.png` : '/public/site-assets/login-logo.png',
      loginBg: base ? `${base}/public/site-assets/login-background.png` : '/public/site-assets/login-background.png',
      headerLogo: base ? `${base}/public/site-assets/header-logo.png` : '/public/site-assets/header-logo.png',
    };
  }

  const openImageSettingsModal = () => {
    setImageSettingsModalOpen(true);
    setPendingImageFiles({ loginLogo: null, loginBg: null, headerLogo: null });
    setImagePreviewError({ loginLogo: false, loginBg: false, headerLogo: false });
    const urls = getFixedAssetUrls();
    setImageForm({
      loginLogo: urls.loginLogo,
      loginBg: urls.loginBg,
      headerLogo: urls.headerLogo,
    });
  };

  const saveImageSettings = async () => {
    const hasAny = pendingImageFiles.loginLogo || pendingImageFiles.loginBg || pendingImageFiles.headerLogo;
    if (!hasAny) {
      setImageSettingsModalOpen(false);
      return;
    }
    setImageFormSaving(true);
    try {
      await uploadSiteAssetsMulti({
        login_logo: pendingImageFiles.loginLogo || undefined,
        login_background: pendingImageFiles.loginBg || undefined,
        header_logo: pendingImageFiles.headerLogo || undefined,
      });
      const version = String(Date.now());
      try {
        localStorage.setItem(SITE_ASSETS_VERSION_KEY, version);
      } catch (e) { }
      setAssetsVersion(Number(version));
      setHeaderLogoHide(false);
      setPendingImageFiles({ loginLogo: null, loginBg: null, headerLogo: null });
      setImageSettingsModalOpen(false);
      showToast('이미지 설정이 저장되었습니다.');
    } catch (e) {
      showToast(e.message || '저장에 실패했습니다.', 'error');
    } finally {
      setImageFormSaving(false);
    }
  };

  const handleImageFile = (field, file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setPendingImageFiles((prev) => ({ ...prev, [field]: file }));
    setImagePreviewError((prev) => ({ ...prev, [field]: false }));
    const reader = new FileReader();
    reader.onload = () => {
      setImageForm((f) => ({ ...f, [field]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const sidebarBg = isMerchant ? 'bg-[#f9faff]' : 'bg-[#f3f3f3]';
  const sidebarBorder = isMerchant ? 'border-[var(--brand-primary-soft-strong)]' : 'border-[#e8e8e8]';
  const headerBg = isMerchant ? 'bg-[#f9faff]' : 'bg-[#f3f3f3]';
  const headerBorder = isMerchant ? 'border-[var(--brand-primary-soft-strong)]' : 'border-[#e8e8e8]';
  const menuActiveBg = isMerchant ? 'bg-[var(--brand-primary-soft-strong)]' : 'bg-[#e0e0e0]';
  const menuHoverBg = isMerchant ? 'hover:bg-[var(--brand-primary-soft-strong)]' : 'hover:bg-[#e8e8e8]';

  // 로그인 유효 시간: 60분에서 1초마다 감소, 0이 되면 로그아웃 처리 후 로그인 페이지로
  useEffect(() => {
    let isLoggingOut = false;
    const timer = setInterval(async () => {
      const exp = sessionStorage.getItem(SESSION_EXPIRY_KEY) || localStorage.getItem('ls_session_expiry');
      let left = exp ? Math.max(0, Math.ceil((Number(exp) - Date.now()) / 1000)) : 0;
      if (left > SESSION_MAX_SECONDS) {
        const cappedExpiry = String(Date.now() + SESSION_MAX_SECONDS * 1000);
        try {
          sessionStorage.setItem(SESSION_EXPIRY_KEY, cappedExpiry);
          localStorage.setItem('ls_session_expiry', cappedExpiry);
        } catch (e) { }
        left = SESSION_MAX_SECONDS;
      }
      setSessionSecondsLeft(left);
      if (left <= 0 && !isLoggingOut) {
        isLoggingOut = true;
        clearInterval(timer);
        try {
          if (basePath === '/admin') {
            const logoutFn = await loadAdminLogout();
            await logoutFn();
          } else if (basePath === '/cp' || basePath === '/app') {
            const logoutFn = await loadCpLogout();
            await logoutFn();
          }
        } catch (e) {
          console.error('로그아웃 API 호출 실패:', e);
        }
        dispatch(setOtpVerified(false));
        dispatch(checkLogin(false));
        navigate(basePath);
        showToast('로그인 시간이 만료되었습니다. 다시 로그인해 주세요.', 'error');
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [basePath, dispatch, navigate, showToast]);

  /** 메뉴 클릭·버튼·기간 조회·모달 내부 버튼 등 사용자 활동 시 세션 60분으로 연장 (capture로 stopPropagation 있는 모달 내부 클릭도 수신) */
  useEffect(() => {
    const handleActivity = () => extendSessionOnActivity();
    const opts = { capture: true };
    document.addEventListener('click', handleActivity, opts);
    document.addEventListener('keydown', handleActivity, opts);
    document.addEventListener('scroll', handleActivity, { passive: true });
    return () => {
      document.removeEventListener('click', handleActivity, opts);
      document.removeEventListener('keydown', handleActivity, opts);
      document.removeEventListener('scroll', handleActivity);
    };
  }, []);

  /** 세션 연장 (저장소 + UI 갱신). showToastOption: true면 연장 토스트 표시 */
  const extendSession = (showToastOption = false) => {
    const nextExpiry = String(Date.now() + SESSION_MAX_SECONDS * 1000);
    try {
      sessionStorage.setItem(SESSION_EXPIRY_KEY, nextExpiry);
      localStorage.setItem('ls_session_expiry', nextExpiry);
    } catch (e) { }
    setSessionSecondsLeft(SESSION_MAX_SECONDS);
    if (showToastOption) showToast('로그인 시간이 1시간 연장되었습니다.');
  };

  const handleSessionExtend = () => extendSession(true);

  /** 사용자 활동(클릭·키보드 등) 시 세션 60분으로 연장 — 모든 행동에서 리셋 */
  const extendSessionOnActivity = () => extendSession(false);

  const handleLogout = async () => {
    if (!window.confirm('로그아웃 하시겠습니까?')) return;
    try {
      if (basePath === '/admin') {
        const logoutFn = await loadAdminLogout();
        await logoutFn();
      } else if (basePath === '/cp') {
        const logoutFn = await loadCpLogout();
        await logoutFn();
      } else if (basePath === '/app') {
        const logoutFn = await loadAppTerminalLogout();
        await logoutFn();
      }
    } catch (e) { }
    resetShowAll();
    dispatch(setOtpVerified(false));
    dispatch(checkLogin(false));
    navigate(basePath);
  };

  const handleLogoClick = () => {
    // PNP 단일 CP는 관리, 그 외 가맹점은 통합정산으로 이동
    // 관리자 헤더 로고 클릭 시: 정산 페이지로 이동 / 앱: 대시보드로 이동
    if (isMerchant) {
      navigate(ADMIN_SINGLE_CP_MODE ? `${basePath}/delivery` : `${basePath}/integrated-settlement`);
    } else if (basePath === '/admin') {
      navigate(`${basePath}/settlement`);
    } else {
      navigate(`${basePath}/dashboard`);
    }
  };

  const openPasswordModal = () => {
    setCurrentPassword('');
    setNewPassword('');
    setNewPasswordConfirm('');
    setPasswordError('');
    setIsPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (!currentPassword.trim()) {
      setPasswordError('현재 비밀번호를 입력해 주세요.');
      return;
    }
    if (!newPassword.trim()) {
      setPasswordError('새 비밀번호를 입력해 주세요.');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setPasswordError('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    // 관리자/가맹점 비밀번호 정책 분리
    if (isMerchant) {
      // 가맹점: 기존처럼 4자 이상만 체크
      if (newPassword.length < 4) {
        setPasswordError('새 비밀번호는 4자 이상이어야 합니다.');
        return;
      }
      try {
        setPasswordSubmitting(true);
        const changePasswordFn = await loadCpChangePassword();
        await changePasswordFn({
          currentPassword: currentPassword.trim(),
          newPassword: newPassword.trim(),
        });
        showToast('비밀번호가 변경되었습니다.');
        closePasswordModal();
      } catch (err) {
        setPasswordError(err.message || '비밀번호 변경에 실패했습니다.');
      } finally {
        setPasswordSubmitting(false);
      }
    } else {
      // 관리자: 정책 정규식 검사 (최소 8자, 영문+숫자+특수문자)
      const policy = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
      if (!policy.test(newPassword)) {
        setPasswordError('새 비밀번호는 8자 이상이며 영문, 숫자, 특수문자를 모두 포함해야 합니다.');
        return;
      }
      try {
        setPasswordSubmitting(true);
        const changePasswordFn = await loadAdminChangePassword();
        await changePasswordFn({
          currentPassword: currentPassword.trim(),
          newPassword: newPassword.trim(),
        });
        showToast('비밀번호가 변경되었습니다.');
        closePasswordModal();
      } catch (err) {
        setPasswordError(err.message || '비밀번호 변경에 실패했습니다.');
      } finally {
        setPasswordSubmitting(false);
      }
    }
  };

  const sessionLabel = `로그인 유지 ${formatSessionTime(sessionSecondsLeft)}`;
  const headerUserLabel =
    basePath === '/admin'
      ? (adminSupplierInfo?.name || userLabel)
      : basePath === '/cp'
        ? userLabel
        : userLabel;

  // 가맹점/관리자: 햄버거 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    if (!burgerMenuOpen) return;
    const handleClickOutside = (e) => {
      if (burgerRef.current && !burgerRef.current.contains(e.target)) {
        setBurgerMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [burgerMenuOpen]);

  return (
    <div className="flex flex-col h-[100dvh] overflow-hidden bg-[#f2f4f6]">
      {/* 헤더 툴팁 (관리자 전용) */}
      {basePath === '/admin' && (
        <>
          <Tooltip id="header-balance-tooltip" place="right" positionStrategy="absolute" className="max-w-[400px] text-[11px] z-[9999] whitespace-pre-line" isOpen={!pgHeaderModalOpen && (showAllOpen || hoveredTooltipId === 'header-balance')} setIsOpen={() => { }} />
          <Tooltip id="header-extend-tooltip" place="left" positionStrategy="absolute" className="max-w-[400px] text-[11px] z-[9999] whitespace-pre-line" isOpen={!pgHeaderModalOpen && (showAllOpen || hoveredTooltipId === 'header-extend')} setIsOpen={() => { }} />
          {menuItems.filter((m) => m.tooltip).map((m) => (
            <Tooltip key={m.path} id={`header-menu-${m.path.replace(/^\//, '')}-tooltip`} place="bottom" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-pre-line" isOpen={!pgHeaderModalOpen && (showAllOpen || hoveredTooltipId === `header-menu-${m.path.replace(/^\//, '')}`)} setIsOpen={() => { }} />
          ))}

          {/* PG 헤더/모달 전용 툴팁 (PG_HEADER_MODE === 1, 목업 UI) */}
          <Tooltip id="header-pg-remaining-tooltip" place="left" positionStrategy="absolute" className="max-w-[320px] text-[11px] z-[9999] whitespace-pre-line" isOpen={!pgHeaderModalOpen && (showAllOpen || hoveredTooltipId === 'header-pg-remaining')} setIsOpen={() => { }} />
          <Tooltip id="pg-modal-total-tooltip" place="right" positionStrategy="absolute" className="max-w-[320px] text-[11px] z-[30000] whitespace-pre-line" isOpen={pgHeaderModalOpen && (showAllOpen || hoveredTooltipId === 'pg-modal-total')} setIsOpen={() => { }} />
          <Tooltip id="pg-modal-total-limit-tooltip" place="right" positionStrategy="absolute" className="max-w-[320px] text-[11px] z-[30000] whitespace-pre-line" isOpen={pgHeaderModalOpen && (showAllOpen || hoveredTooltipId === 'pg-modal-total-limit')} setIsOpen={() => { }} />
          <Tooltip id="pg-modal-total-balance-tooltip" place="right" positionStrategy="absolute" className="max-w-[320px] text-[11px] z-[30000] whitespace-pre-line" isOpen={pgHeaderModalOpen && (showAllOpen || hoveredTooltipId === 'pg-modal-total-balance')} setIsOpen={() => { }} />
          <Tooltip id="pg-modal-manage-tooltip" place="right" positionStrategy="absolute" className="max-w-[320px] text-[11px] z-[30000] whitespace-pre-line" isOpen={pgHeaderModalOpen && (showAllOpen || hoveredTooltipId === 'pg-modal-manage')} setIsOpen={() => { }} />
        </>
      )}
      {/* 헤더 툴팁 (가맹점 전용) */}
      {basePath === '/cp' && (
        <>
          <Tooltip id="header-sugi-tooltip" place="right" positionStrategy="absolute" middlewares={[offset(10), shift({ padding: 5 })]} className="max-w-[400px] text-[11px] z-[9999] whitespace-pre-line" isOpen={!pgHeaderModalOpen && (showAllOpen || hoveredTooltipId === 'header-sugi')} setIsOpen={() => { }} />
          <Tooltip id="header-extend-tooltip" place="left" positionStrategy="absolute" className="max-w-[400px] text-[11px] z-[9999] whitespace-pre-line" isOpen={!pgHeaderModalOpen && (showAllOpen || hoveredTooltipId === 'header-extend')} setIsOpen={() => { }} />
        </>
      )}
      {/* ========== 헤더 (최상단, 로고 포함) ========== */}
      <header className={`h-[60px] ${headerBg} flex items-center border-b ${headerBorder} shrink-0 z-[100] relative overflow-visible`}>
        {/* 로고 영역 (기존과 동일 200px) */}
        <div
          className="w-[200px] flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity shrink-0"
          onClick={handleLogoClick}
        >
          {!headerLogoHide && (
            <img
              key={assetsVersion}
              src={getHeaderLogoAssetUrl()}
              alt=""
              className="object-contain"
              style={getHeaderLogoStyle()}
              onError={() => setHeaderLogoHide(true)}
            />
          )}
        </div>
        {/* 관리자: 로고 오른쪽 모계좌/PG 헤더 */}
        {basePath === '/admin' && PG_HEADER_MODE === 0 && (
          <div className="ml-4 flex items-center">
            <div
              data-tooltip-id="header-balance-tooltip"
              data-tooltip-html={masterAccountBalanceSource === "KG_INICIS" ? "KG이니시스 지급대행 MID 기준 잔액입니다.<br/>동일 MID를 사용하는 서비스 금액이 합산될 수 있습니다." + (masterAccountBalancePeriod ? "<br/>조회기간: " + masterAccountBalancePeriod : "") : "지급 가능한 가상계좌 잔액입니다.<br/>상위 PG-가맹점 수수료 차액의 누적 합계입니다."}
              onMouseEnter={() => !showAllOpen && setHoveredTooltipId('header-balance')}
              onMouseLeave={() => setHoveredTooltipId(null)}
              className="px-2.5 py-1 rounded-xl bg-white border border-[#e5e8eb] flex items-baseline gap-3 cursor-help"
            >
              <span className="text-[12px] font-medium text-[#4e5968] flex items-center gap-1">
                잔액
                <HelpCircle size={12} className="text-[#8b95a1]" />
              </span>
              <span className="text-[15px] font-extrabold text-[#191f28] tabular-nums text-right">
                {masterAccountBalanceLoading
                  ? "조회 중..."
                  : masterAccountBalanceNoData
                    ? "내역 없음"
                    : formatHeaderBalance(masterAccountBalance) || "-"}
              </span>
            </div>
          </div>
        )}
        {basePath === '/admin' && PG_HEADER_MODE === 1 && (
          <div className="flex items-center">
            <div
              onClick={() => { setHoveredTooltipId(null); setPgHeaderModalOpen(true); }}
              className="flex items-center bg-white/95 border border-[#e5e8eb] rounded-xl px-2.5 py-1.5 cursor-pointer hover:border-[#5b67f1]/40 hover:shadow-md transition-all group"
            >
              <div className={`flex items-center gap-2 ${PG_LIMIT_MODE === 1 ? 'pr-2' : ''}`}>
                {pgHeaderItems.map((pg) => (
                  <div
                    key={pg.id}
                    className={`flex flex-col px-2.5 py-1 rounded-lg transition-all border ${pg.isActive
                      ? 'bg-slate-800 text-white border-transparent shadow-sm scale-105'
                      : 'bg-slate-50 text-slate-400 border-transparent opacity-70'
                      }`}
                  >
                    <div className="flex items-center gap-1 mb-0.5">
                      {pgHeaderIsReady ? (
                        <>
                          <span className={`text-[10px] font-medium uppercase tracking-tight ${pg.isActive ? 'text-white/80' : 'text-slate-400'}`}>
                            {pg.name}
                          </span>
                          {pg.isActive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
                        </>
                      ) : (
                        <>
                          <span className="h-[10px] w-[40px] bg-slate-200 rounded animate-pulse" />
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-200 animate-pulse" />
                        </>
                      )}
                    </div>
                    {pgHeaderIsReady ? (
                      <span
                        className={`text-[13px] font-medium leading-none tabular-nums text-right w-full ${pg.isActive ? 'text-white' : 'text-slate-700'}`}
                      >
                        {pg.balance === null || pg.balance === undefined
                          ? '알 수 없음'
                          : Number.isFinite(Number(pg.balance))
                            ? Number(pg.balance).toLocaleString('ko-KR')
                            : '알 수 없음'}
                      </span>
                    ) : (
                      <div className="h-[14px] w-[80px] bg-slate-200 rounded animate-pulse" />
                    )}
                  </div>
                ))}
              </div>
              {PG_LIMIT_MODE === 1 ? (
                <div className="flex flex-col justify-center pl-3 min-w-[160px] border-l border-slate-200/70">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-0.5 text-[11px] font-medium text-slate-800">
                      <span>잔여 한도</span>
                      <span
                        data-tooltip-id="header-pg-remaining-tooltip"
                        data-tooltip-html="선택된 PG사의 잔여 결제 한도입니다.<br/>헤더를 클릭하면 PG사를 변경할 수 있습니다."
                        onMouseEnter={() => !showAllOpen && !pgHeaderModalOpen && setHoveredTooltipId('header-pg-remaining')}
                        onMouseLeave={() => setHoveredTooltipId(null)}
                        className="inline-flex cursor-help"
                      >
                        <HelpCircle size={11} className="text-slate-400" />
                      </span>
                    </div>
                    {pgHeaderIsReady ? (
                      <span className="text-[11px] font-medium text-slate-800 tabular-nums">
                        {formatToEok(activePG.limit - activePG.usage)} / {formatToEok(activePG.limit)}
                      </span>
                    ) : (
                      <div className="h-[11px] w-[70px] bg-slate-200 rounded animate-pulse" />
                    )}
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    {pgHeaderIsReady ? (
                      <div
                        className="h-full bg-slate-800 rounded-full transition-all duration-700 shadow-[0_0_8px_rgba(15,23,42,0.55)]"
                        style={{
                          width: `${Math.max((activePG.usage / activePG.limit) * 100, 0)}%`,
                        }}
                      />
                    ) : (
                      <div className="h-full bg-slate-300 rounded-full animate-pulse w-1/2" />
                    )}
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        )}
        {/* 관리자: 헤더 중앙 네비게이션 (좌우 콘텐츠와 무관하게 완전 중앙 정렬) */}
        {basePath === '/admin' && (
          <nav className="absolute inset-y-0 left-1/2 -translate-x-1/2 transform flex items-center justify-center gap-1 pointer-events-none">
            <div className="flex items-center gap-1 pointer-events-auto">
              {menuItems.map((item) => {
                const href = `${basePath}${item.path}`;
                if (item.openInPopup) {
                  const fullUrl = typeof window !== 'undefined' ? `${window.location.origin}${href}` : href;
                  let popupOptions = '';
                  if (typeof window !== 'undefined') {
                    const w = item.popupWidth ?? 450;
                    const h = item.popupHeight ?? 600;
                    const availLeft = window.screen?.availLeft ?? 0;
                    const availTop = window.screen?.availTop ?? 0;
                    const availWidth = window.screen?.availWidth ?? window.screen?.width ?? 800;
                    const availHeight = window.screen?.availHeight ?? window.screen?.height ?? 600;
                    const left = Math.max(availLeft, availLeft + (availWidth - w) / 2);
                    const top = Math.max(availTop, availTop + (availHeight - h) / 2);
                    popupOptions = `width=${w},height=${h},scrollbars=yes,resizable=yes,left=${Math.round(left)},top=${Math.round(top)}`;
                  }
                  const menuTooltipId = `header-menu-${item.path.replace(/^\//, '')}`;
                  return (
                    <button
                      key={item.path}
                      type="button"
                      data-tooltip-id={item.tooltip ? `${menuTooltipId}-tooltip` : undefined}
                      data-tooltip-html={item.tooltip}
                      onMouseEnter={() => item.tooltip && !showAllOpen && setHoveredTooltipId(menuTooltipId)}
                      onMouseLeave={() => item.tooltip && setHoveredTooltipId(null)}
                      onClick={() => window.open(fullUrl, 'sugiPopup', popupOptions)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[15px] font-bold text-[#4e5968] hover:bg-[#e8e8e8] hover:text-[#191f28] transition-all ${item.tooltip ? 'cursor-help' : ''}`}
                    >
                      <span className="text-[#8b95a1]">{item.icon}</span>
                      {item.label}
                    </button>
                  );
                }
                const menuTooltipId = `header-menu-${item.path.replace(/^\//, '')}`;
                return (
                  <NavLink
                    key={item.path}
                    to={href}
                    data-tooltip-id={item.tooltip ? `${menuTooltipId}-tooltip` : undefined}
                    data-tooltip-html={item.tooltip}
                    onMouseEnter={() => item.tooltip && !showAllOpen && setHoveredTooltipId(menuTooltipId)}
                    onMouseLeave={() => item.tooltip && setHoveredTooltipId(null)}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 py-2 rounded-xl text-[15px] font-bold transition-all ${item.tooltip ? 'cursor-help' : ''} ${isActive ? 'bg-[#e0e0e0] text-[#191f28]' : 'text-[#4e5968] hover:bg-[#e8e8e8] hover:text-[#191f28]'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className={isActive ? 'text-[#191f28]' : 'text-[#8b95a1]'}>{item.icon}</span>
                        {item.label}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </nav>
        )}
        {/* 가맹점(v1): 헤더 중앙 네비게이션 (업무/영업자/수기결제) */}
        {isMerchantV1 && (
          <nav className="absolute inset-y-0 left-1/2 -translate-x-1/2 transform flex items-center justify-center gap-1 pointer-events-none">
            <div className="flex items-center gap-1 pointer-events-auto">
              {menuItems.map((item) => {
                const href = `${basePath}${item.path}`;
                if (item.openInPopup) {
                  const fullUrl = typeof window !== 'undefined' ? `${window.location.origin}${href}` : href;
                  let popupOptions = '';
                  if (typeof window !== 'undefined') {
                    const w = item.popupWidth ?? 450;
                    const h = item.popupHeight ?? 800;
                    const availLeft = window.screen?.availLeft ?? 0;
                    const availTop = window.screen?.availTop ?? 0;
                    const availWidth = window.screen?.availWidth ?? window.screen?.width ?? 800;
                    const availHeight = window.screen?.availHeight ?? window.screen?.height ?? 600;
                    const left = Math.max(availLeft, availLeft + (availWidth - w) / 2);
                    const top = Math.max(availTop, availTop + (availHeight - h) / 2);
                    popupOptions = `width=${w},height=${h},scrollbars=yes,resizable=yes,left=${Math.round(left)},top=${Math.round(top)}`;
                  }
                  return (
                    <button
                      key={item.path}
                      type="button"
                      onClick={() => window.open(fullUrl, 'sugiPopup', popupOptions)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-[15px] font-bold text-[#4e5968] hover:bg-[var(--brand-primary-soft-strong)] hover:text-[#191f28] transition-all"
                    >
                      <span className="text-[#8b95a1]">{item.icon}</span>
                      {item.label}
                    </button>
                  );
                }
                return (
                  <NavLink
                    key={item.path}
                    to={href}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 py-2 rounded-xl text-[15px] font-bold transition-all ${isActive ? 'bg-[var(--brand-primary-soft-strong)] text-[#191f28]' : 'text-[#4e5968] hover:bg-[var(--brand-primary-soft-strong)] hover:text-[#191f28]'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className={isActive ? 'text-[#191f28]' : 'text-[#8b95a1]'}>{item.icon}</span>
                        {item.label}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </nav>
        )}
        {/* 나머지 헤더 컨텐츠 영역 (가맹점: 제목+수기결제 | 관리자: 우측 컨트롤만, 줄바꿈 방지) */}
        <div className={`flex items-center shrink-0 overflow-visible ${basePath === '/admin' ? 'w-[300px] min-w-[300px] ml-auto pr-4 justify-end' : 'flex-1 px-6 justify-between'}`}>
          {basePath !== '/admin' && (
            <div className="flex items-center gap-4">
              <h1 className="text-[18px] font-bold text-[#191f28]">
                {isMerchant ? `${MERCHANT_BRAND_NAME} 가맹점페이지` : `${ADMIN_BRAND_NAME} 관리자페이지`}
              </h1>
              {isMerchant && !isMerchantV1 && MERCHANT_MANUAL_PAYMENT_ENABLED && (
                <button
                  type="button"
                  data-tooltip-id="header-sugi-tooltip"
                  data-tooltip-html="영업자가 수기결제하는 방식이나, 가맹점의 수기결제 지원 기능입니다.<br/>영업자 결제와 동일한 방식으로, 카드 없이 카드정보만으로 결제할 수 있습니다."
                  onMouseEnter={() => !showAllOpen && setHoveredTooltipId('header-sugi')}
                  onMouseLeave={() => setHoveredTooltipId(null)}
                  onClick={openSugiPopup}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--brand-primary)] text-white rounded-xl font-bold text-[14px] hover:opacity-90 shrink-0 cursor-help"
                >
                  <CreditCard size={16} />
                  수기결제
                </button>
              )}
            </div>
          )}
          <div className="flex items-center gap-4 shrink-0 whitespace-nowrap overflow-visible">
            {/* 로그인 유효 시간 표시 + 연장 */}
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[14px] font-bold text-[#4e5968]">{sessionLabel}</span>
              <button
                type="button"
                data-tooltip-id={(basePath === '/admin' || basePath === '/cp') ? 'header-extend-tooltip' : undefined}
                data-tooltip-html={(basePath === '/admin' || basePath === '/cp') ? '로그인 유지 시간이 1시간으로 초기화됩니다.' : undefined}
                onMouseEnter={(basePath === '/admin' || basePath === '/cp') ? () => !showAllOpen && setHoveredTooltipId('header-extend') : undefined}
                onMouseLeave={(basePath === '/admin' || basePath === '/cp') ? () => setHoveredTooltipId(null) : undefined}
                onClick={handleSessionExtend}
                className={`px-3 py-1.5 rounded-lg text-[13px] font-bold text-[var(--brand-primary)] border border-[var(--brand-primary)] bg-white hover:bg-[var(--brand-primary-soft)] transition-colors ${(basePath === '/admin' || basePath === '/cp') ? 'cursor-help' : ''}`}
              >
                연장
              </button>
            </div>
            <div className="h-4 w-px bg-[#e5e8eb] shrink-0" />
            <span className="font-bold text-[16px] text-[#191f28] shrink-0">{headerUserLabel}</span>
            {/* 가맹점: 햄버거 메뉴 (비밀번호 변경, 비밀번호 수정, 로그아웃) */}
            {isMerchant ? (
              <div className="relative" ref={burgerRef}>
                <button
                  type="button"
                  onClick={() => setBurgerMenuOpen((v) => !v)}
                  className="p-2 rounded-lg text-[#4e5968] hover:bg-[var(--brand-primary-soft-strong)] hover:text-[#191f28] transition-colors"
                  aria-label="메뉴"
                >
                  <Menu size={24} />
                </button>
                {burgerMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 py-1 min-w-[160px] bg-white rounded-xl border border-[#e5e8eb] shadow-lg z-[200] flex flex-col">
                    <button
                      type="button"
                      onClick={() => {
                        setBurgerMenuOpen(false);
                        openPasswordModal();
                      }}
                      className="w-full px-4 py-2.5 text-[14px] font-medium text-[#191f28] text-left hover:bg-[#f2f4f6] first:rounded-t-xl shrink-0"
                    >
                      비밀번호 변경
                    </button>
                    {!isMerchantV1 && (
                      <button
                        type="button"
                        onClick={() => {
                          setBurgerMenuOpen(false);
                          setDriverPasswordModalOpen(true);
                          setDriverTerminal(null);
                          setDriverNewPassword('');
                          (async () => {
                            try {
                              const fetchList = await loadCpTerminalList();
                              const data = await fetchList({});
                              const rows = data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);
                              const list = Array.isArray(rows) ? rows : [];
                              if (list.length >= 1) {
                                const first = list[0];
                                setDriverTerminal({
                                  id: first.id ?? first.terminal_id ?? first.terminalId,
                                  driverId: first.terminalId ?? first.terminal_id ?? first.driverId ?? '-',
                                });
                              } else {
                                setDriverTerminal(false);
                              }
                            } catch (e) {
                              showToast(e.message || '목록을 불러오지 못했습니다.', 'error');
                              setDriverTerminal(false);
                            }
                          })();
                        }}
                        className="w-full px-4 py-2.5 text-[14px] font-medium text-[#191f28] text-left hover:bg-[#f2f4f6] shrink-0"
                      >
                        영업자 비밀번호 변경
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setBurgerMenuOpen(false);
                        handleLogout();
                      }}
                      className="w-full px-4 py-2.5 text-[14px] font-medium text-[#191f28] text-left hover:bg-[#f2f4f6] last:rounded-b-xl shrink-0"
                    >
                      로그아웃
                    </button>
                  </div>
                )}
              </div>
            ) : basePath === '/admin' ? (
              /* 관리자: 햄버거 메뉴 (비밀번호 변경, 로그아웃) */
              <div className="relative" ref={burgerRef}>
                <button
                  type="button"
                  onClick={() => setBurgerMenuOpen((v) => !v)}
                  className="p-2 rounded-lg text-[#4e5968] hover:bg-[#e0e0e0] hover:text-[#191f28] transition-colors"
                  aria-label="메뉴"
                >
                  <Menu size={24} />
                </button>
                {burgerMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 py-1 min-w-[160px] bg-white rounded-xl border border-[#e5e8eb] shadow-lg z-[200] flex flex-col">
                    <button
                      type="button"
                      onClick={() => {
                        setBurgerMenuOpen(false);
                        openImageSettingsModal();
                      }}
                      className="w-full px-4 py-2.5 text-[14px] font-medium text-[#191f28] text-left hover:bg-[#f2f4f6] first:rounded-t-xl shrink-0"
                    >
                      로고, 이미지 변경
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setBurgerMenuOpen(false);
                        openPasswordModal();
                      }}
                      className="w-full px-4 py-2.5 text-[14px] font-medium text-[#191f28] text-left hover:bg-[#f2f4f6] shrink-0"
                    >
                      비밀번호 변경
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setBurgerMenuOpen(false);
                        handleLogout();
                      }}
                      className="w-full px-4 py-2.5 text-[14px] font-medium text-[#191f28] text-left hover:bg-[#f2f4f6] last:rounded-b-xl shrink-0"
                    >
                      로그아웃
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* 앱: 기존 버튼 노출 */
              <div className="flex overflow-hidden rounded-lg border border-[#d1d6db]">
                <button
                  type="button"
                  onClick={openPasswordModal}
                  className="px-3 py-2 rounded-l-lg rounded-r-none text-[14px] font-medium text-[#4e5968] border-r border-[#d1d6db] bg-white hover:bg-[#f2f4f6] hover:text-[#191f28] hover:border-[#adb5bd] transition-colors"
                >
                  비밀번호 변경
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="px-3 py-2 rounded-r-lg rounded-l-none text-[14px] font-medium text-[#4e5968] bg-white hover:bg-[#f2f4f6] hover:text-[#191f28] hover:border-[#adb5bd] transition-colors border-l-0"
                >
                  로그아웃
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ========== 사이드바 + 본문 ========== */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* 사이드바 - 가맹점·관리자 모두 사이드 메뉴 미노출 (헤더에 메뉴 표시) */}
        {!isMerchant && basePath !== '/admin' && (
          <aside className={`sticky left-0 z-[90] w-[200px] ${sidebarBg} flex flex-col shrink-0 border-b ${sidebarBorder}`}>
            <nav className="flex-1 px-4 py-4 space-y-1">
              {menuItems.map((item) => {
                const href = `${basePath}${item.path}`;
                const fullUrl = typeof window !== 'undefined' ? `${window.location.origin}${href}` : href;
                let popupOptions = '';
                if (item.openInPopup && typeof window !== 'undefined') {
                  const w = item.popupWidth ?? 450;
                  const h = item.popupHeight ?? 600;
                  const availLeft = window.screen?.availLeft ?? 0;
                  const availTop = window.screen?.availTop ?? 0;
                  const availWidth = window.screen?.availWidth ?? window.screen?.width ?? 800;
                  const availHeight = window.screen?.availHeight ?? window.screen?.height ?? 600;
                  const left = Math.max(availLeft, availLeft + (availWidth - w) / 2);
                  const top = Math.max(availTop, availTop + (availHeight - h) / 2);
                  popupOptions = `width=${w},height=${h},scrollbars=yes,resizable=yes,left=${Math.round(left)},top=${Math.round(top)}`;
                }

                if (item.openInPopup) {
                  return (
                    <button
                      key={item.path}
                      type="button"
                      onClick={() => window.open(fullUrl, 'sugiPopup', popupOptions)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[18px] font-bold transition-all text-left text-[#4e5968] ${menuHoverBg} hover:text-[#191f28]`}
                    >
                      <span className="text-[#8b95a1]">{item.icon}</span>
                      {item.label}
                    </button>
                  );
                }
                return (
                  <NavLink
                    key={item.path}
                    to={href}
                    className={({ isActive }) =>
                      `w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[18px] font-bold transition-all ${isActive
                        ? `${menuActiveBg} text-[#191f28]`
                        : `text-[#4e5968] ${menuHoverBg} hover:text-[#191f28]`
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className={isActive ? 'text-[#191f28]' : 'text-[#8b95a1]'}>{item.icon}</span>
                        {item.label}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </nav>
          </aside>
        )}

        {/* 본문 - 헤더(60px) 제외한 나머지 높이만 사용, 100dvh에 딱 맞춤, 스크롤 없음 */}
        <main className="flex-1 flex flex-col min-w-0 min-h-0 bg-white relative overflow-hidden" style={{ maxHeight: 'calc(100dvh - 60px)' }}>
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <Outlet />
          </div>
          {basePath === '/admin' && (
            <footer className="shrink-0 py-[0.5rem] px-[1.5rem] flex items-center justify-between text-[12px] text-[#8b95a1] border-t border-[#f2f4f6]">
              <button
                type="button"
                onClick={toggleShowAll}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded  ${showAllOpen ? 'bg-[var(--brand-primary)] text-white' : 'bg-[#e5e8eb] text-[#8b95a1] hover:bg-[#d0d7de]'
                  }`}
                title="전체 툴팁 보기 (n)"
              >
                <HelpCircle size={14} strokeWidth={2} />
                <span>툴팁안내 (n)</span>
              </button>
              <span>{BRAND_COPYRIGHT}</span>
            </footer>
          )}
        </main>
      </div>

      {/* PG 헤더용 다중 PG 관리 모달 (목업, PG_HEADER_MODE === 1 전용) */}
      {basePath === '/admin' && PG_HEADER_MODE === 1 && pgHeaderModalOpen && (
        <div className="fixed inset-0 z-[20000] flex items-center justify-center px-4 py-6">
          <div
            className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
            onClick={() => {
              setPgHeaderModalOpen(false);
              setEditingPgId(null);
            }}
          />
          <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-white/30 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-[var(--brand-primary-soft-strong)]/60 to-white">
              <div className="flex items-center gap-3">
                <div className="bg-[#5b67f1] p-2 rounded-xl text-white shadow-lg">
                  <Activity size={18} />
                </div>
                <div className="text-left">
                  <h3 className="font-black text-[15px] text-slate-900 leading-tight">
                    PG 결제사 관리
                  </h3>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPgHeaderModalOpen(false);
                  setEditingPgId(null);
                }}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
              {/* 통합 한도 현황 */}
              {PG_LIMIT_MODE === 1 && (
                <div className="bg-slate-800 rounded-2xl p-5 text-white shadow-xl">
                  <div className="flex items-center justify-between mb-4 px-1">
                    <div className="flex items-center gap-2">
                      <BarChart3 size={18} className="text-[var(--brand-secondary)]" />
                      <span className="text-[12px] font-medium uppercase text-slate-200">
                        전체 통합 한도 현황
                      </span>
                      <span
                        data-tooltip-id="pg-modal-total-tooltip"
                        data-tooltip-html="등록된 PG사의 한도를 합산한 통합 현황입니다.<br/>프로그레스바는 '사용량 / 전체 한도' 비율입니다."
                        onMouseEnter={() => !showAllOpen && pgHeaderModalOpen && setHoveredTooltipId('pg-modal-total')}
                        onMouseLeave={() => setHoveredTooltipId(null)}
                        className="inline-flex cursor-help"
                      >
                        <HelpCircle size={12} className="text-slate-400" />
                      </span>
                    </div>
                    <span className="text-2xl font-black text-[var(--brand-secondary)] leading-none tabular-nums">
                      {pgTotalStats.limit > 0
                        ? Math.floor((pgTotalStats.usage / pgTotalStats.limit) * 100)
                        : 0}
                      %
                    </span>
                  </div>
                  <div className="space-y-3 px-1">
                    <div className="w-full h-2 bg-white/15 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[var(--brand-secondary)] rounded-full transition-all duration-700 shadow-[0_0_12px_rgba(129,140,248,0.5)]"
                        style={{
                          width:
                            pgTotalStats.limit > 0
                              ? `${Math.max(
                                (pgTotalStats.usage / pgTotalStats.limit) * 100,
                                0,
                              )}%`
                              : '0%',
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-1 text-right">
                      <div className="flex flex-col items-end">
                        <span className="text-[10px] text-slate-300 uppercase mb-0.5 inline-flex items-center justify-end gap-1">
                          <span>전체 한도</span>
                          <span
                            data-tooltip-id="pg-modal-total-limit-tooltip"
                            data-tooltip-html="전체 한도(억 단위)입니다.<br/>각 PG 한도의 합계입니다."
                            onMouseEnter={() => !showAllOpen && pgHeaderModalOpen && setHoveredTooltipId('pg-modal-total-limit')}
                            onMouseLeave={() => setHoveredTooltipId(null)}
                            className="inline-flex cursor-help"
                          >
                            <HelpCircle size={10} className="text-slate-400" />
                          </span>
                        </span>
                        <span className="text-[15px] text-slate-100 tabular-nums">
                          {formatToEok(pgTotalStats.limit)}
                        </span>
                      </div>
                      <div className="flex flex-col items-end border-l border-white/15 pl-4">
                        <span className="text-[10px] text-slate-300 uppercase mb-0.5 inline-flex items-center justify-end gap-1">
                          <span>전체 잔여량</span>
                          <span
                            data-tooltip-id="pg-modal-total-balance-tooltip"
                            data-tooltip-html="전체 잔여량(억 단위)입니다.<br/>\\(전체 한도 - 전체 사용량\\) 입니다."
                            onMouseEnter={() => !showAllOpen && pgHeaderModalOpen && setHoveredTooltipId('pg-modal-total-balance')}
                            onMouseLeave={() => setHoveredTooltipId(null)}
                            className="inline-flex cursor-help"
                          >
                            <HelpCircle size={10} className="text-slate-400" />
                          </span>
                        </span>
                        <span className="text-[15px] text-slate-50 tabular-nums">
                          {formatToEok(pgTotalStats.limit - pgTotalStats.usage)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 개별 PG 리스트 */}
              <div className="space-y-3">
                <h4 className="text-[12px] font-medium text-slate-500 uppercase pl-1 text-left flex items-center gap-1.5">
                  <span>{PG_LIMIT_MODE === 1 ? '운영 PG사 선택 및 한도 관리' : '운영 PG사 선택'}</span>
                  <span
                    data-tooltip-id="pg-modal-manage-tooltip"
                    data-tooltip-html={PG_LIMIT_MODE === 1
                      ? "PG사를 전환하거나, 각 PG의 한도(목업)를 수정할 수 있습니다.<br/>실제 결제 PG 선택 기능 연동 전까지는 목업 동작입니다."
                      : "운영할 PG사를 선택할 수 있습니다."}
                    onMouseEnter={() => !showAllOpen && pgHeaderModalOpen && setHoveredTooltipId('pg-modal-manage')}
                    onMouseLeave={() => setHoveredTooltipId(null)}
                    className="inline-flex cursor-help"
                  >
                    <HelpCircle size={12} className="text-slate-400" />
                  </span>
                </h4>
                {pgList.map((pg) => (
                  <div
                    key={pg.id}
                    className={`border-2 rounded-xl p-4 flex items-center gap-3 transition-all ${pg.isActive
                      ? 'border-[var(--brand-secondary)] bg-[var(--brand-primary-soft-strong)]/40 shadow-sm'
                      : 'border-slate-100 bg-white hover:border-slate-200'
                      }`}
                  >
                    {PG_LIMIT_MODE === 1 ? (
                      <>
                        {/* 사용중/전환 버튼 */}
                        <button
                          type="button"
                          onClick={() => !pg.isActive && handlePgHeaderSwitch(pg.id)}
                          disabled={pgSwitchLoading}
                          className={`w-12 h-9 rounded-lg text-[10px] font-black transition-all shrink-0 shadow-sm ${pg.isActive
                            ? 'bg-[#5b67f1] text-white cursor-default'
                            : pgSwitchLoading
                              ? 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-70'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                        >
                          {pgSwitchLoading && !pg.isActive ? '전환중' : pg.isActive ? '사용중' : '전환'}
                        </button>

                        {/* PG 이름 */}
                        <span
                          className={`font-black text-[16px] leading-tight shrink-0 w-[140px] truncate ${pg.isActive ? 'text-[var(--brand-primary-active)]' : 'text-slate-800'
                            }`}
                        >
                          {pg.name}
                        </span>

                        {/* 한도 / 잔여량 / 프로그레스 */}
                        <div className="flex-1 min-w-0 max-w-[250px] flex flex-col gap-1.5 pt-0.5">
                          <div className="flex items-center justify-between h-[24px]">
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() =>
                                  setEditingPgId((prev) => (prev === pg.id ? null : pg.id))
                                }
                                className={`p-1 rounded-md transition-colors ${editingPgId === pg.id
                                  ? 'text-[var(--brand-secondary)]'
                                  : 'text-slate-300 hover:text-[var(--brand-secondary)]'
                                  }`}
                              >
                                {editingPgId === pg.id ? (
                                  <Save size={14} strokeWidth={2.5} />
                                ) : (
                                  <Settings2 size={14} strokeWidth={2.5} />
                                )}
                              </button>
                              {editingPgId === pg.id ? (
                                <div className="flex items-center border border-[var(--brand-secondary)] rounded px-1.5 py-0.5 bg-white w-[90px]">
                                  <input
                                    type="number"
                                    step="0.1"
                                    value={pg.limit / 100000000}
                                    onChange={(e) =>
                                      handlePgHeaderLimitChange(pg.id, e.target.value)
                                    }
                                    className="w-full text-[12px] font-black text-right focus:outline-none bg-transparent"
                                    autoFocus
                                  />
                                  <span className="text-[10px] text-slate-400 ml-0.5">
                                    억
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[12px] text-[var(--brand-secondary)]/80 leading-none tabular-nums">
                                  {formatToEok(pg.limit)}
                                </span>
                              )}
                            </div>
                            <span
                              className={`text-[18px] font-black leading-none tabular-nums ${pg.isActive ? 'text-[var(--brand-primary-active)]' : 'text-slate-800'
                                }`}
                            >
                              {formatToEok(pg.limit - pg.usage)}
                            </span>
                          </div>
                          <div className="w-full h-2 rounded-full overflow-hidden bg-slate-100 border border-slate-100">
                            <div
                              className={`h-full transition-all duration-700 ${pg.isActive
                                ? 'bg-[var(--brand-primary-soft-strong)]0 shadow-[0_0_8px_rgba(91,103,241,0.45)]'
                                : 'bg-slate-300'
                                }`}
                              style={{
                                width: `${Math.max((pg.usage / pg.limit) * 100, 0)}%`,
                              }}
                            />
                          </div>
                        </div>

                        {/* 정산방식 */}
                        <div className="shrink-0 relative pt-2">
                          <span className="absolute -top-0.5 left-2 px-1 bg-white text-[10px] font-medium text-slate-400 z-[1]">정산방식</span>
                          <select
                            value={pg.roundingMode || 'round'}
                            onChange={(e) => handlePgRoundingModeChange(pg.id, e.target.value)}
                            className="text-[12px] font-bold text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:border-[var(--brand-secondary)] cursor-pointer"
                          >
                            <option value="round">반올림</option>
                            <option value="ceil">올림</option>
                            <option value="floor">버림</option>
                          </select>
                        </div>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => !pg.isActive && handlePgHeaderSwitch(pg.id)}
                          disabled={pgSwitchLoading}
                          className={`w-12 h-9 rounded-lg text-[10px] font-black transition-all shrink-0 shadow-sm ${pg.isActive
                            ? 'bg-[#5b67f1] text-white cursor-default'
                            : pgSwitchLoading
                              ? 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-70'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                        >
                          {pgSwitchLoading && !pg.isActive ? '전환중' : pg.isActive ? '사용중' : '전환'}
                        </button>
                        <span className={`font-black text-[16px] leading-tight truncate ${pg.isActive ? 'text-[var(--brand-primary-active)]' : 'text-slate-800'}`}>
                          {pg.name}
                        </span>
                        <div className="ml-auto shrink-0 relative pt-2">
                          <span className="absolute -top-0.5 left-2 px-1 bg-white text-[10px] font-medium text-slate-400 z-[1]">정산방식</span>
                          <select
                            value={pg.roundingMode || 'round'}
                            onChange={(e) => handlePgRoundingModeChange(pg.id, e.target.value)}
                            className="text-[12px] font-bold text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:border-[var(--brand-secondary)] cursor-pointer"
                          >
                            <option value="round">반올림</option>
                            <option value="ceil">올림</option>
                            <option value="floor">버림</option>
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50/60 text-center">
              <button
                type="button"
                onClick={() => {
                  setPgHeaderModalOpen(false);
                  setEditingPgId(null);
                }}
                className="w-full py-2.5 bg-white border border-slate-200 text-slate-600 font-black rounded-xl text-[13px] shadow-sm hover:bg-slate-50 active:scale-95 transition-all"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 비밀번호 변경 모달 */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[480px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]">
            <div className="px-8 py-6 flex items-center justify-between border-b border-[#f2f4f6]">
              <h2 className="text-[24px] font-black text-[#191f28]">비밀번호 변경</h2>
              <button
                type="button"
                onClick={closePasswordModal}
                className="p-2 text-[#adb5bd] hover:text-[#191f28]"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handlePasswordSubmit} className="flex flex-col flex-1">
              <div className="py-5 px-8">
                <div className="p-4 bg-[var(--brand-primary-soft)] rounded-2xl flex items-center gap-3 border border-[var(--brand-primary-border)] mb-6">
                  <div className="p-2 bg-white rounded-lg">
                    <Lock size={16} className="text-[var(--brand-primary)]" />
                  </div>
                  <span className="text-[13px] font-bold text-[var(--brand-primary)]">
                    현재 비밀번호를 입력한 후 새 비밀번호를 설정해 주세요.
                  </span>
                </div>
                <div className="space-y-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-bold text-[#8b95a1]">현재 비밀번호</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="현재 비밀번호를 입력하세요"
                      className="w-full px-4 py-3 rounded-xl border border-[#e5e8eb] bg-white text-[15px] text-[#191f28] placeholder:text-[#adb5bd] focus:border-[var(--brand-primary)] focus:outline-none"
                      autoComplete="current-password"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-bold text-[#8b95a1]">새 비밀번호</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="새 비밀번호를 입력하세요"
                      className="w-full px-4 py-3 rounded-xl border border-[#e5e8eb] bg-white text-[15px] text-[#191f28] placeholder:text-[#adb5bd] focus:border-[var(--brand-primary)] focus:outline-none"
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-bold text-[#8b95a1]">새 비밀번호 확인</label>
                    <input
                      type="password"
                      value={newPasswordConfirm}
                      onChange={(e) => setNewPasswordConfirm(e.target.value)}
                      placeholder="새 비밀번호를 다시 입력하세요"
                      className="w-full px-4 py-3 rounded-xl border border-[#e5e8eb] bg-white text-[15px] text-[#191f28] placeholder:text-[#adb5bd] focus:border-[var(--brand-primary)] focus:outline-none"
                      autoComplete="new-password"
                    />
                  </div>
                  {passwordError && (
                    <p className="text-[13px] text-[#f04452] font-medium">{passwordError}</p>
                  )}
                </div>
              </div>
              <div className="px-8 py-6 bg-[#f9fafb] border-t border-[#f2f4f6] flex gap-3">
                <button
                  type="button"
                  onClick={closePasswordModal}
                  className="flex-1 py-4 bg-[#e5e8eb] text-[#4e5968] rounded-2xl font-bold text-[16px]"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={passwordSubmitting}
                  className="flex-1 py-4 bg-[var(--brand-primary)] text-white rounded-2xl font-bold text-[16px] shadow-lg shadow-[var(--brand-primary)]/20 disabled:opacity-60"
                >
                  {passwordSubmitting ? '변경 중...' : '비밀번호 변경'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 관리자 전용: 로고·이미지 변경 모달 (업로드 + 미리보기) */}
      {basePath === '/admin' && imageSettingsModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white w-[560px] max-w-[95vw] max-h-[90vh] rounded-2xl shadow-[0_24px_48px_-12px_rgba(0,0,0,0.25)] overflow-hidden flex flex-col">
            {/* 헤더 */}
            <div className="px-6 py-5 flex items-center justify-between shrink-0 bg-gradient-to-b from-[#f8fafc] to-white border-b border-[#e5e8eb]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#eef4ff] flex items-center justify-center">
                  <ImageIcon size={20} className="text-[var(--brand-primary)]" />
                </div>
                <div>
                  <h2 className="text-[18px] font-bold text-[#191f28] tracking-tight">로고, 이미지 변경</h2>
                  <p className="text-[12px] text-[#8b95a1] mt-0.5">로그인·헤더에 노출되는 이미지를 변경합니다</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setImageSettingsModalOpen(false)}
                className="p-2.5 text-[#8b95a1] hover:text-[#191f28] rounded-xl hover:bg-[#f2f4f6] transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1 min-h-0">
              {/* 1. 로그인화면 로고 */}
              <div className="rounded-xl border border-[#e5e8eb] bg-[#fafbfc] p-4 hover:border-[#d1d9e0] transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-md bg-[var(--brand-primary)] text-white text-[12px] font-bold flex items-center justify-center">1</span>
                  <label className="text-[14px] font-semibold text-[#191f28]">로그인화면 로고 이미지</label>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-28 h-28 rounded-xl border-2 border-dashed border-[#e5e8eb] bg-white flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
                    {imageForm.loginLogo && !imagePreviewError.loginLogo ? (
                      <img src={imageForm.loginLogo} alt="미리보기" className="w-full h-full object-contain p-1" onError={() => setImagePreviewError((p) => ({ ...p, loginLogo: true }))} onLoad={() => setImagePreviewError((p) => ({ ...p, loginLogo: false }))} />
                    ) : (
                      <span className="text-[12px] text-[#adb5bd]">미리보기</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageFile('loginLogo', f); e.target.value = ''; }} className="hidden" id="upload-login-logo" />
                    <label htmlFor="upload-login-logo" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-[14px] font-semibold cursor-pointer hover:bg-[var(--brand-primary-hover)] active:scale-[0.98] transition-all shadow-sm">
                      <Upload size={18} />
                      파일 선택
                    </label>
                  </div>
                </div>
              </div>
              {/* 2. 로그인화면 배경 - 잠시 주석처리 */}
              {/* <div className="rounded-xl border border-[#e5e8eb] bg-[#fafbfc] p-4 hover:border-[#d1d9e0] transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-md bg-[var(--brand-primary)] text-white text-[12px] font-bold flex items-center justify-center">2</span>
                  <label className="text-[14px] font-semibold text-[#191f28]">로그인화면 배경 이미지</label>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-28 h-28 rounded-xl border-2 border-dashed border-[#e5e8eb] bg-white flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
                    {imageForm.loginBg && !imagePreviewError.loginBg ? (
                      <img src={imageForm.loginBg} alt="미리보기" className="w-full h-full object-cover" onError={() => setImagePreviewError((p) => ({ ...p, loginBg: true }))} onLoad={() => setImagePreviewError((p) => ({ ...p, loginBg: false }))} />
                    ) : (
                      <span className="text-[12px] text-[#adb5bd]">미리보기</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageFile('loginBg', f); e.target.value = ''; }} className="hidden" id="upload-login-bg" />
                    <label htmlFor="upload-login-bg" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-[14px] font-semibold cursor-pointer hover:bg-[var(--brand-primary-hover)] active:scale-[0.98] transition-all shadow-sm">
                      <Upload size={18} />
                      파일 선택
                    </label>
                  </div>
                </div>
              </div> */}
              {/* 2. 헤더 로고 */}
              <div className="rounded-xl border border-[#e5e8eb] bg-[#fafbfc] p-4 hover:border-[#d1d9e0] transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-md bg-[var(--brand-primary)] text-white text-[12px] font-bold flex items-center justify-center">2</span>
                  <label className="text-[14px] font-semibold text-[#191f28]">헤더 로고 이미지</label>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-28 h-28 rounded-xl border-2 border-dashed border-[#e5e8eb] bg-white flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
                    {imageForm.headerLogo && !imagePreviewError.headerLogo ? (
                      <img src={imageForm.headerLogo} alt="미리보기" className="w-full h-full object-contain p-1" onError={() => setImagePreviewError((p) => ({ ...p, headerLogo: true }))} onLoad={() => setImagePreviewError((p) => ({ ...p, headerLogo: false }))} />
                    ) : (
                      <span className="text-[12px] text-[#adb5bd]">미리보기</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageFile('headerLogo', f); e.target.value = ''; }} className="hidden" id="upload-header-logo" />
                    <label htmlFor="upload-header-logo" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-[14px] font-semibold cursor-pointer hover:bg-[var(--brand-primary-hover)] active:scale-[0.98] transition-all shadow-sm">
                      <Upload size={18} />
                      파일 선택
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-[#f8fafc] border-t border-[#e5e8eb] flex gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setImageSettingsModalOpen(false)}
                disabled={imageFormSaving}
                className="flex-1 py-3 rounded-xl font-semibold text-[14px] disabled:opacity-60 bg-white border border-[#e5e8eb] text-[#4e5968] hover:bg-[#f2f4f6] hover:border-[#d1d9e0] transition-colors"
              >
                취소
              </button>
              <button
                type="button"
                onClick={saveImageSettings}
                disabled={imageFormSaving}
                className="flex-1 py-3 rounded-xl font-semibold text-[14px] disabled:opacity-60 bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)] shadow-sm hover:shadow transition-all"
              >
                {imageFormSaving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 가맹점 전용: 비밀번호 수정 모달 (햄버거 메뉴에서 열림, 페이지 이동 없음) */}
      {isMerchant && driverPasswordModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[480px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]">
            <div className="px-8 py-6 flex items-center justify-between border-b border-[#f2f4f6]">
              <h2 className="text-[24px] font-black text-[#191f28]">영업자 비밀번호 변경</h2>
              <button
                type="button"
                onClick={() => {
                  setDriverPasswordModalOpen(false);
                  setDriverNewPassword('');
                  setDriverTerminal(null);
                }}
                className="p-2 text-[#adb5bd] hover:text-[#191f28]"
              >
                <X size={24} />
              </button>
            </div>
            <div className="py-5 px-8">
              {driverTerminal === null ? (
                <div className="py-8 text-center text-[14px] text-[#8b95a1]">불러오는 중...</div>
              ) : driverTerminal === false ? (
                <div className="py-8 text-center text-[14px] text-[#8b95a1]">영업자 계정이 없습니다.</div>
              ) : (
                <>
                  <div className="p-4 bg-[var(--brand-primary-soft)] rounded-2xl flex items-center gap-3 border border-[var(--brand-primary-border)] mb-6">
                    <div className="p-2 bg-white rounded-lg">
                      <Truck size={16} className="text-[var(--brand-primary)]" />
                    </div>
                    <span className="text-[13px] font-bold text-[var(--brand-primary)]">
                      영업자 계정의 비밀번호를 변경합니다.
                    </span>
                  </div>
                  <div className="space-y-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-bold text-[#8b95a1]">영업자 ID</label>
                      <div className="w-full px-4 py-3 rounded-xl border border-transparent bg-[#f2f4f6] text-[15px] font-medium text-[#4e5968] cursor-default select-none">
                        {driverTerminal.driverId}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-bold text-[#8b95a1]">새 비밀번호</label>
                      <input
                        type="password"
                        placeholder="새 비밀번호를 입력하세요"
                        value={driverNewPassword}
                        onChange={(e) => setDriverNewPassword(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-[#e5e8eb] bg-white text-[15px] text-[#191f28] placeholder:text-[#adb5bd] focus:border-[var(--brand-primary)] focus:outline-none"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="px-8 py-6 bg-[#f9fafb] border-t border-[#f2f4f6] flex gap-3">
              {(driverTerminal === null || driverTerminal === false) ? (
                <button
                  type="button"
                  onClick={() => {
                    setDriverPasswordModalOpen(false);
                    setDriverNewPassword('');
                    setDriverTerminal(null);
                  }}
                  className="flex-1 py-4 bg-[#e5e8eb] text-[#4e5968] rounded-2xl font-bold text-[16px]"
                >
                  닫기
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setDriverPasswordModalOpen(false);
                      setDriverNewPassword('');
                      setDriverTerminal(null);
                    }}
                    className="flex-1 py-4 bg-[#e5e8eb] text-[#4e5968] rounded-2xl font-bold text-[16px]"
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!driverNewPassword?.trim()) {
                        showToast('새 비밀번호를 입력해 주세요.', 'error');
                        return;
                      }
                      const id = driverTerminal.id;
                      if (id == null) {
                        showToast('계정 정보가 올바르지 않습니다.', 'error');
                        return;
                      }
                      try {
                        setDriverPasswordSubmitting(true);
                        const changeFn = await loadTerminalChangePassword();
                        await changeFn({ id: Number(id), newPassword: driverNewPassword.trim() });
                        setDriverPasswordModalOpen(false);
                        setDriverNewPassword('');
                        setDriverTerminal(null);
                        showToast('비밀번호가 변경되었습니다.');
                      } catch (e) {
                        showToast(e.message || '비밀번호 변경에 실패했습니다.', 'error');
                      } finally {
                        setDriverPasswordSubmitting(false);
                      }
                    }}
                    disabled={driverPasswordSubmitting}
                    className="flex-1 py-4 bg-[var(--brand-primary)] text-white rounded-2xl font-bold text-[16px] shadow-lg shadow-[var(--brand-primary)]/20 disabled:opacity-60"
                  >
                    {driverPasswordSubmitting ? '변경 중...' : '비밀번호 변경'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MainLayout;
